import {useMemo} from 'react';
import {useQueries, useQuery, useQueryClient} from '@tanstack/react-query';

import {ICategory} from 'coreStore/data/categories/types';
import {useCommerceAPI} from 'commerceAPI/contexts';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {useRequestClass} from 'coreHooks/index';
import useLanguageCode from 'coreHooks/locales/useLanguageCode';

import {
	getCategoriesQueryKey,
	createCategoryResult,
	ICategoryResult,
	fetchCategoryTree,
} from 'coreUtils/categories';

export default (categoryIds: string[]): ICategoryResult[] => {
	const {isCORSRequest} = useRequestClass();
	const {managerRepository} = useGeneralProvider();
	const localesMgr = managerRepository.getLocalesMgr();
	const queryClient = useQueryClient();
	const commerceApi = useCommerceAPI();
	const languageCode = useLanguageCode();

	const {data: categories = []} = useQuery<Record<string, ICategory>, Error, ICategory[]>({
		queryKey: getCategoriesQueryKey(languageCode),
		queryFn: () => ({} as Record<string, ICategory>),
		enabled: false,
		staleTime: Infinity,
		select: (allCategories) =>
			categoryIds.reduce<ICategory[]>((arr, cgId) => {
				const stored = allCategories[cgId];

				if (stored && !stored._notFound) {
					arr.push(stored);
				}

				return arr;
			}, []),
	});

	// Subscribed (not read from the cache during render): fetched once the tree is loaded.
	const {data: missingIds = []} = useQuery<Record<string, ICategory>, Error, string[]>({
		queryKey: getCategoriesQueryKey(languageCode),
		queryFn: () => ({} as Record<string, ICategory>),
		enabled: false,
		staleTime: Infinity,
		select: (cached) => (cached?.root ? categoryIds.filter((cgId) => cgId && !cached[cgId]) : []),
	});

	useQueries({
		queries: missingIds.map((cgId) => ({
			queryKey: ['category-fetch', cgId, languageCode],
			queryFn: () => fetchCategoryTree(cgId, commerceApi, queryClient, languageCode),
			staleTime: Infinity,
			retry: false,
		})),
	});

	return useMemo(
		() => categories.map(cat => createCategoryResult(cat, {localesMgr, isCORSRequest, queryClient, languageCode})),
		[categories, isCORSRequest, queryClient, languageCode]
	);
};
